import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'redux/store';
import { ProjectOverviewFieldsFragment } from 'api/graphql-types';

import { Button } from 'components/button';
import { FilterButton, IFilterItem } from 'components/filter-button/filter-button';
import { CardNews } from 'components/card';
import { EmptyState } from 'components/empty-state/empty-state';

import styles from './projects-overview.module.scss';
import { NumberParam, useQueryParams } from 'use-query-params';
import { searchProjects } from 'api/algolia';
import useProjectsFilters from './data/use-projects-filter';
import clsx from 'clsx';
import { PROJECTS_OVERVIEW_LIMIT } from './data';
import { ProjectsAlgoliaFragment } from 'redux/stores/projects';
import { PAGE_QUERY_KEY, THEME_QUERY_KEY, ThemeArrayParam, usePagination, useTheme } from 'utils/hooks/queries';

type FetchProjects = (page: number, themeIds: string[]) => Promise<void>;

type ProjectsFetchProps = {
	page: number;
	themeIds: string[];
};

export interface IProjectOverviewProps {
	module: ProjectOverviewFieldsFragment;
}

const ProjectOverview = (props: IProjectOverviewProps): React.JSX.Element => {
	const router = useRouter();
	const { formatMessage } = useIntl();

	const [query, setQuery] = useQueryParams({
		[PAGE_QUERY_KEY]: NumberParam,
		[THEME_QUERY_KEY]: ThemeArrayParam
	});

	const overviewRef = useRef<HTMLDivElement>(null);
	const { projects, projectsCount } = useAppSelector((state) => state.projects);

	const [requests, setRequests] = useState<ProjectsAlgoliaFragment[]>(projects || []);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(false);
	const [requestCount, setRequestCount] = useState<number>(projectsCount);

	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { currentPage, setCurrentPage, Pagination } = usePagination(PROJECTS_OVERVIEW_LIMIT);
	const { selectedThemes, setSelectedThemesParam } = useTheme();

	const fetchProjects: FetchProjects = async (page, themeIds): Promise<void> => {
		setRequestIsLoading(true);

		try {
			const { results, total } = await searchProjects(
				'',
				{
					themesIds: themeIds
				},
				{
					sort: 'desc',
					page,
					limit: PROJECTS_OVERVIEW_LIMIT,
					locale: router.locale
				}
			);

			setRequests(results);
			setRequestCount(total);
		} catch {
			setRequests([]);
			setRequestCount(0);
		}

		setRequestIsLoading(false);
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 140;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.scrollY - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handleDeleteFilter = (): void => {
		setQuery({
			thema: null,
			pagina: null
		});
		setTempSelectedThemes([]);
		handleFetchProjects(undefined, true);
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			setCurrentPage(page);
			handleFetchProjects({ page });
		}
	};

	const handleFetchProjects = async (data?: Partial<ProjectsFetchProps>, reset?: boolean): Promise<void> => {
		const selectedPage = reset ? 1 : data?.page || currentPage;

		const selectedThemeIds = reset ? [] : data?.themeIds || selectedThemes.map((theme) => String(theme.id));

		scrollToTop();
		setHasFiltered(data !== undefined);
		fetchProjects(selectedPage, selectedThemeIds);
	};

	const projectsFilters = useProjectsFilters(props.module, (themeIds: string[]) =>
		handleFetchProjects({ page: 1, themeIds })
	);

	useEffect(() => {
		handleFetchProjects(undefined);
		setTempSelectedThemes(selectedThemes.map((theme) => ({ id: theme.id, label: theme.label })));
	}, [initialQuery]);

	useEffect(() => {
		if (!hasFiltered && !initialQuery && query[THEME_QUERY_KEY]) {
			setInitialQuery(query);
		}
	}, [query]);

	const [tempSelectedThemes, setTempSelectedThemes] = useState<IFilterItem[]>([]);

	const applyFilters = (): void => {
		setSelectedThemesParam(tempSelectedThemes.length > 0 ? tempSelectedThemes : null);
		handleFetchProjects({ page: 1, themeIds: tempSelectedThemes.map((theme) => String(theme.id)) });
	};

	return (
		<div className={styles.ProjectOverview} ref={overviewRef}>
			<div className={styles.wrapper}>
				<div className={styles.filterBar}>
					<div className={styles.filterContent}>
						<span className={styles.label}>{props.module.filterLabel}</span>
						<div className={styles.filterButtons}>
							<span className={styles.themeFilter}>
								<FilterButton
									title={formatMessage({ id: 'modules.projectOverview.themeFilter.title' })}
									items={projectsFilters[0].options as IFilterItem[]}
									onChange={applyFilters}
									onSelectedItemsChanged={setTempSelectedThemes}
									selectedItems={tempSelectedThemes}
								/>
							</span>
							{!!selectedThemes.length && (
								<span className={styles.removeFilters}>
									<Button
										title={formatMessage(
											{ id: 'modules.newsOverview.deleteFilters' },
											{ amount: tempSelectedThemes.length }
										)}
										variant="tertiary-dark"
										onClick={handleDeleteFilter}
									/>
								</span>
							)}
						</div>
					</div>

					<div className={styles.amountOfResults}>
						{formatMessage({ id: 'global.results' }, { results: requestCount })}
					</div>
				</div>
				<div
					className={clsx(styles.content, requestIsLoading && styles.resultsLoading)}
					style={{
						opacity: requestIsLoading ? '0.2' : 1,
						height: requests?.length === 9 ? '1350px' : '300px'
					}}
				>
					{requests?.length > 0 ? (
						requests?.map((project, index) => (
							<div key={index}>
								<CardNews
									id={`project-overview-${index}`}
									bodyText={project?.bodyText?.bodyText || ''}
									excerpt={project?.header?.excerpt || ''}
									publishedAt={project?.firstPublishedAt}
									slug={project?.slug || ''}
									title={project?.title || ''}
									cover={project.header.cover?.data?.attributes || project.header.cover}
									key={index}
									type="projecten"
									priority={index < 3}
									largeImage
								/>
							</div>
						))
					) : (
						<div className={styles.emptyState}>
							<EmptyState onClearFilters={handleDeleteFilter} isLoading={requestIsLoading} />
						</div>
					)}
				</div>
			</div>
			<div className={styles.pagination}>
				<Pagination totalCount={requestCount} onPageChange={handlePageChange} />
			</div>
		</div>
	);
};

export default ProjectOverview;
